<template>
	<div class="video-player-container" :class="{ fullscreen: isFullscreen }">
		<div
			class="video-wrapper w-100 h-100"
			@mousemove="showControls"
			@mouseleave="hideControls"
		>
			<div v-if="loading" class="video-loader">
				<div class="spinner"></div>
			</div>
			<video
				ref="videoElement"
				class="video-element"
				:src="videoSrc"
				@loadedmetadata="onLoadedMetadata"
				@timeupdate="onTimeUpdate"
				@ended="onEnded"
				@click="togglePlay"
				:preload="'auto'"
				@focus="onVideoFocus"
				@blur="onVideoBlur"
				tabindex="0"
				:id="id"
			>
				Your browser does not support the video tag.
			</video>

			<!-- Video Controls -->
			<div class="video-controls" :class="{ visible: controlsVisible }">
				<!-- Progress Bar -->
				<div class="progress-container">
					<div class="progress-bar" @click="seek" ref="progressBar">
						<div
							class="progress-fill"
							:style="{ width: progressPercentage + '%' }"
						></div>
						<div
							class="progress-handle"
							:style="{ left: progressPercentage + '%' }"
						></div>

						<!-- Markers -->
						<div
							v-for="marker in markers"
							:key="marker.id"
							class="progress-marker"
							:style="{ left: (marker.time / duration) * 100 + '%' }"
							@click.stop="seekToMarker(marker.time)"
						>
							<svg
								viewBox="0 0 24 24"
								width="20"
								height="24"
								:style="{
									'--marker-color':
										marker.color === 'blue' ? '#54A7FF' : '#D88924',
								}"
							>
								<path
									d="M6 2h12a1 1 0 0 1 1 1v9c0 .33-.16.64-.44.83l-6.56 4.44a1 1 0 0 1-1.11 0L4.33 12.8A1 1 0 0 1 4 12V3a1 1 0 0 1 1-1z"
									:fill="marker.color === 'blue' ? '#54A7FF' : '#D88924'"
								/>
							</svg>
						</div>
					</div>
				</div>

				<!-- Control Buttons -->
				<div class="controls-row">
					<div class="controls-left">
						<!-- Play/Pause -->
						<button class="control-btn" @click="togglePlay">
							<svg v-if="!isPlaying" viewBox="0 0 24 24" width="20" height="20">
								<path
									d="M8 5v14l11-7z"
									fill="none"
									stroke="white"
									stroke-width="3"
									stroke-linejoin="round"
								/>
							</svg>
							<svg v-else viewBox="0 0 24 24" width="20" height="20">
								<path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
							</svg>
						</button>

						<!-- Replay 10s -->
						<button class="control-btn" @click="backward">
							<svg viewBox="0 0 24 24" width="20" height="20">
								<path
									fill="currentColor"
									d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
									stroke-width="3"
								/>
							</svg>
						</button>

						<!-- Forward 10s -->
						<button class="control-btn" @click="forward">
							<svg viewBox="0 0 24 24" width="20" height="20">
								<path
									fill="currentColor"
									d="M18 13c0 3.31-2.69 6-6 6s-6-2.69-6-6 2.69-6 6-6v4l5-5-5-5v4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8h-2z"
									stroke-width="3"
								/>
							</svg>
						</button>

						<!-- Time Display -->
						<div class="time-display">
							{{ formatTime(currentTime) }}/{{ formatTime(duration) }}
						</div>
					</div>

					<div class="controls-right">
						<!-- Playback Speed -->
						<div class="speed-control">
							<button class="control-btn" @click="toggleSpeedMenu">
								{{ playbackRate }}x
							</button>
							<div v-if="speedMenuVisible" class="speed-menu">
								<div
									v-for="speed in speedOptions"
									:key="speed"
									class="speed-option"
									:class="{ active: playbackRate === speed }"
									@click="setPlaybackRate(speed)"
								>
									{{ speed }}x
								</div>
							</div>
						</div>

						<!-- Mute/Unmute -->
						<button class="control-btn" @click="toggleMute">
							<svg
								v-if="!isMuted && volume > 0.5"
								viewBox="0 0 24 24"
								width="16"
								height="16"
							>
								<path
									fill="currentColor"
									d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
								/>
							</svg>
							<svg
								v-else-if="!isMuted && volume > 0"
								viewBox="0 0 24 24"
								width="16"
								height="16"
							>
								<path
									fill="currentColor"
									d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"
								/>
							</svg>
							<svg v-else viewBox="0 0 24 24" width="16" height="16">
								<path
									fill="currentColor"
									d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
								/>
							</svg>
						</button>

						<!-- Fullscreen -->
						<button class="control-btn" @click="toggleFullscreen">
							<svg
								v-if="!isFullscreen"
								viewBox="0 0 24 24"
								width="20"
								height="20"
							>
								<path
									fill="currentColor"
									d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
								/>
							</svg>
							<svg v-else viewBox="0 0 24 24" width="20" height="20">
								<path
									fill="currentColor"
									d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "VideoPlayer",
	props: {
		videoSrc: {
			type: String,
			required: false,
		},
		id: {
			type: String,
			default: "",
		},
		marker1: {
			type: Object,
			default: () => ({ color: "blue", data: [] }),
		},
		marker2: {
			type: Object,
			default: () => ({ color: "orange", data: [] }),
		},
		tooglePlay: {
			type: Boolean,
			default: false,
		},
		videoDuration: {
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			hasFocus: false,
			loading: true,
			isPlaying: false,
			currentTime: 0,
			duration: 0,
			volume: 1,
			isMuted: false,
			playbackRate: 1,
			isFullscreen: false,
			controlsVisible: true,
			controlsTimeout: null,
			speedMenuVisible: false,
			speedOptions: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
		};
	},
	watch: {
		tooglePlay(newVal) {
			if (newVal) {
				this.isPlaying = true;
				this.getVideoElement().play();
			} else {
				this.isPlaying = false;
				this.getVideoElement().pause();
			}
		},
		videoDuration(newVal) {
			if (newVal > 0) {
				this.duration = newVal;
			}
		},
	},

	computed: {
		progressPercentage() {
			return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
		},
		markers() {
			const combined = [];

			const markerGroups = [
				{ group: this.marker1, baseId: 100, name: "Agent Proctoring" },
				{ group: this.marker2, baseId: 200, name: "Browser Proctoring" },
			];

			markerGroups.forEach(({ group, baseId }) => {
				if (group?.data?.length) {
					group.data.forEach((time, index) => {
						combined.push({
							id: baseId + index,
							time,
							color: group.color || "blue",
						});
					});
				}
			});

			return combined;
		},
	},
	methods: {
		onVideoFocus() {
			console.log("it hase focus");
			this.hasFocus = true;
		},
		onVideoBlur() {
			this.hasFocus = false;
		},
		getVideoElement() {
			return this.$refs.videoElement || document.getElementById(this.id);
		},
		togglePlay() {
			const video = this.getVideoElement();
			if (this.isPlaying) {
				video.pause();
			} else {
				video.play();
			}
			this.isPlaying = !this.isPlaying;
			if (!this.isPlaying) {
				this.$emit("pause");
			}
		},
		forward() {
			const video = this.getVideoElement();
			video.currentTime += 10;
		},
		backward() {
			const video = this.getVideoElement();
			video.currentTime -= 10;
		},
		seek(event) {
			const progressBar = this.$refs.progressBar;
			const rect = progressBar.getBoundingClientRect();
			const percent = (event.clientX - rect.left) / rect.width;
			const video = this.getVideoElement();
			video.currentTime = percent * this.duration;
		},
		seekToMarker(time) {
			const video = this.getVideoElement();
			video.currentTime = time;
		},
		toggleMute() {
			const video = this.getVideoElement();
			this.isMuted = !this.isMuted;
			video.muted = this.isMuted;
		},
		setPlaybackRate(rate) {
			const video = this.getVideoElement();
			video.playbackRate = rate;
			this.playbackRate = rate;
			this.speedMenuVisible = false;
		},
		toggleSpeedMenu() {
			this.speedMenuVisible = !this.speedMenuVisible;
		},
		toggleFullscreen() {
			const container = this.$el;
			if (!this.isFullscreen) {
				if (container.requestFullscreen) {
					container.requestFullscreen();
				} else if (container.webkitRequestFullscreen) {
					container.webkitRequestFullscreen();
				} else if (container.msRequestFullscreen) {
					container.msRequestFullscreen();
				}
			} else {
				if (document.exitFullscreen) {
					document.exitFullscreen();
				} else if (document.webkitExitFullscreen) {
					document.webkitExitFullscreen();
				} else if (document.msExitFullscreen) {
					document.msExitFullscreen();
				}
			}
		},
		onLoadedMetadata() {
			const video = this.getVideoElement();
			this.duration = video.duration || this.videoDuration;
		},
		onTimeUpdate() {
			const video = this.getVideoElement();
			this.currentTime = video.currentTime;
		},
		onEnded() {
			this.isPlaying = false;
		},
		formatTime(seconds) {
			if (isNaN(seconds)) return "0:00";
			const mins = Math.floor(seconds / 60);
			const secs = Math.floor(seconds % 60);
			return `${mins}:${secs.toString().padStart(2, "0")}`;
		},
		showControls() {
			this.controlsVisible = true;
			clearTimeout(this.controlsTimeout);
			this.controlsTimeout = setTimeout(() => {
				if (this.isPlaying) {
					this.controlsVisible = false;
				}
			}, 3000);
		},
		hideControls() {
			if (this.isPlaying) {
				this.controlsTimeout = setTimeout(() => {
					this.controlsVisible = false;
				}, 1000);
			}
		},
	},
	mounted() {
		const video = this.getVideoElement();

		video.addEventListener("waiting", () => {
			this.loading = true;
		});

		video.addEventListener("canplay", () => {
			this.loading = false;
		});

		video.addEventListener("canplaythrough", () => {
			this.loading = false;
		});

		video.addEventListener("seeking", () => {
			this.loading = true;
		});

		video.addEventListener("seeked", () => {
			this.loading = false;
		});

		const fullscreenChangeHandler = () => {
			this.isFullscreen = !!(
				document.fullscreenElement ||
				document.webkitFullscreenElement ||
				document.msFullscreenElement
			);
		};

		document.addEventListener("fullscreenchange", fullscreenChangeHandler);
		document.addEventListener(
			"webkitfullscreenchange",
			fullscreenChangeHandler
		);
		document.addEventListener("msfullscreenchange", fullscreenChangeHandler);

		video.addEventListener("keydown", (e) => {
			if (!this.hasFocus) return;
			if (e.code === "Space" && e.target === video) {
				e.preventDefault();
				this.togglePlay();
			} else if (e.code === "ArrowLeft") {
				this.backward();
			} else if (e.code === "ArrowRight") {
				this.forward();
			} else if (e.code === "KeyF") {
				this.toggleFullscreen();
			} else if (e.code === "KeyM") {
				this.toggleMute();
			}
		});

		document.addEventListener("click", (e) => {
			if (!this.$el.contains(e.target)) {
				this.speedMenuVisible = false;
			}
		});

		if (this.videoDuration > 0) {
			this.duration = this.videoDuration;
		}
	},
};
</script>

<style scoped>
.video-loader {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 5;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 4px solid rgba(255, 255, 255, 0.3);
	border-top-color: white;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.video-player-container {
	position: relative;
	width: 100%;
	max-width: 800px;
	margin: 0 auto;
	background: #000;
	border-radius: 8px;
	overflow: hidden;
	height: 100%;
}

.video-player-container.fullscreen {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	max-width: none;
	border-radius: 0;
	z-index: 9999;
}

.video-wrapper {
	position: relative;
	width: 100%;
	height: 100%;
}

.video-element {
	width: 100%;
	height: 100%;
	display: block;
}

.video-controls {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
	color: white;
	padding: 20px 16px 16px;
	transition: opacity 0.3s ease;
	opacity: 0;
}

.video-controls.visible {
	opacity: 1;
}

.progress-container {
	margin-bottom: 12px;
}

.progress-bar {
	position: relative;
	height: 4px;
	background: rgba(255, 255, 255, 0.3);
	border-radius: 2px;
	cursor: pointer;
	margin-bottom: 8px;
	overflow: visible;
}

.progress-fill {
	height: 100%;
	background: #007bff;
	border-radius: 2px;
	transition: width 0.1s ease;
}

.progress-handle {
	position: absolute;
	top: 50%;
	width: 12px;
	height: 12px;
	background: #007bff;
	border-radius: 50%;
	transform: translate(-50%, -50%);
	cursor: pointer;
}
.progress-marker {
	position: absolute;
	top: -22px;
	transform: translateX(-50%);
	cursor: pointer;
	z-index: 2;
	transition: all 0.2s ease;
}

.progress-marker:hover {
	z-index: 10;
	transform: translateX(-50%) scale(1.1);
}
.progress-marker:hover svg path {
	stroke: var(--marker-color);
	stroke-width: 1.5px;
	fill-opacity: 0.7;
}

.progress-marker:hover::before {
	background: #0056b3;
	width: 10px;
	height: 7px;
	left: -5px;
	top: -11px;
}

.controls-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.controls-left,
.controls-right {
	display: flex;
	align-items: center;
	gap: 8px;
}

.control-btn {
	background: none;
	border: none;
	color: white;
	padding: 8px;
	border-radius: 4px;
	cursor: pointer;
	transition: background-color 0.2s ease;
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	position: relative;
}

.control-btn:hover {
	background: rgba(255, 255, 255, 0.1);
}

.control-btn.active {
	background: rgba(0, 123, 255, 0.3);
}

.time-display {
	font-size: 13px;
	color: white;
	margin-left: 8px;
}

.speed-control {
	position: relative;
}

.speed-menu {
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	background: rgba(0, 0, 0, 0.9);
	border-radius: 4px;
	padding: 8px 0;
	margin-bottom: 8px;
	min-width: 60px;
}

.speed-option {
	padding: 8px 16px;
	cursor: pointer;
	text-align: center;
	font-size: 12px;
	transition: background-color 0.2s ease;
}

.speed-option:hover {
	background: rgba(255, 255, 255, 0.1);
}

.speed-option.active {
	background: rgba(0, 123, 255, 0.3);
}

/* Mobile responsive */
@media (max-width: 768px) {
	.controls-left,
	.controls-right {
		gap: 4px;
	}

	.control-btn {
		padding: 6px;
	}

	.time-display {
		font-size: 11px;
	}

	.video-controls {
		padding: 16px 12px 12px;
	}
}
</style>
